/**
 * Plan vs actual engine — the single source of truth for every date and status PACE
 * shows. Pure and synchronous: the server uses it for reports and checks, the web app
 * for screens and for optimistic updates.
 *
 * Rules:
 *  1. Every step has a reference date: its baseline (frozen at publish), or its current
 *     plan while the project is still a draft.
 *  2. Forecast of a step:
 *       done              → the actual date
 *       skipped (N/A)     → none; the chain passes straight through
 *       open / on hold    → the latest of: its reference date, the previous step's
 *                           forecast + its duration (working days), and today.
 *     So a slip anywhere carries down the chain to Shipping, and a step that is not
 *     done can never be forecast in the past.
 *  3. The first project milestone (T0) waits for every tool's last step.
 *  4. Slip = forecast (or actual) − reference, in calendar days.
 *  5. A project is SHIPPED when its last step is done; LATE when its end forecast is
 *     past its end baseline; AT_RISK when anything is overdue, at risk or on hold;
 *     otherwise ON_TRACK. Each published project is in exactly one bucket.
 */
import { addWorkingDays, daysBetween, isValidDate, maxDate, type WorkCalendar } from './calendar';
import type { ProjectInput, ProjectPhase, ProjectStatus, StepInput, StepStatus, ToolInput } from './types';
import { PROJECT_STATUSES } from './types';

export interface StepView extends StepInput {
  /** Reference date: baseline, or plan while drafting. */
  ref: string | null;
  /** Null only for skipped steps. */
  forecast: string | null;
  status: StepStatus;
  /** Calendar days late (+) or early (−) against the reference. 0 when not comparable. */
  slip: number;
  /** The step before it is finished, so this one can be worked on now. */
  ready: boolean;
}

export interface ToolView {
  id: string;
  steps: StepView[];
  /** Forecast of the tool's last step. */
  end: string | null;
  /** Worst slip among the tool's steps. */
  slip: number;
  done: boolean;
}

export interface ProjectView {
  id: string;
  phase: ProjectPhase;
  tools: ToolView[];
  milestones: StepView[];
  /** Null for drafts and cancelled projects. */
  status: ProjectStatus | null;
  end: string | null;
  endRef: string | null;
  /** end − endRef in calendar days. */
  slip: number;
  counts: Record<'overdue' | 'dueToday' | 'atRisk' | 'hold' | 'done' | 'total', number>;
}

const isDone = (s: StepInput) => s.state === 'done';
const isSkipped = (s: StepInput) => s.state === 'skipped';

export function stepStatus(
  s: StepInput,
  forecast: string | null,
  ref: string | null,
  today: string,
): StepStatus {
  if (s.state === 'skipped') return 'SKIPPED';
  if (s.state === 'done') return ref && s.actual && s.actual > ref ? 'DONE_LATE' : 'DONE';
  if (s.state === 'hold') return 'HOLD';
  if (ref && ref < today) return 'OVERDUE';
  if (ref && ref === today) return 'DUE_TODAY';
  if (ref && forecast && forecast > ref) return 'AT_RISK';
  return 'UPCOMING';
}

/**
 * Forecast one chain of steps that starts after `anchor`. `anchorDone` says whether
 * whatever comes before the first step is finished (drives `ready`).
 */
export function forecastChain(
  steps: readonly StepInput[],
  anchor: string,
  anchorDone: boolean,
  today: string,
  cal: WorkCalendar,
  useBaseline = true,
): StepView[] {
  let prev = anchor;
  let prevDone = anchorDone;
  return steps.map((s) => {
    const ref = (useBaseline ? s.baseline : null) ?? s.planned;
    if (isSkipped(s)) {
      return { ...s, ref, forecast: null, status: 'SKIPPED' as const, slip: 0, ready: prevDone };
    }
    const ready = prevDone;
    let forecast: string;
    if (isDone(s)) {
      forecast = s.actual ?? ref ?? prev;
    } else {
      const chained = addWorkingDays(prev, Math.max(0, s.days), cal);
      forecast = maxDate(maxDate(ref ?? chained, chained), today);
    }
    const status = stepStatus(s, forecast, ref, today);
    const slip = ref ? daysBetween(ref, forecast) : 0;
    prev = forecast;
    prevDone = isDone(s);
    return { ...s, ref, forecast, status, slip, ready };
  });
}

const lastActive = <T extends StepInput>(steps: readonly T[]): T | undefined =>
  [...steps].reverse().find((s) => !isSkipped(s));

function toolView(
  t: ToolInput,
  start: string,
  today: string,
  cal: WorkCalendar,
  useBaseline: boolean,
): ToolView {
  const steps = forecastChain(t.steps, start, true, today, cal, useBaseline);
  const last = lastActive(steps);
  return {
    id: t.id,
    steps,
    end: last?.forecast ?? null,
    slip: steps.reduce((m, s) => Math.max(m, s.slip), 0),
    done: !last || last.state === 'done',
  };
}

export function projectView(p: ProjectInput, today: string, cal: WorkCalendar): ProjectView {
  const useBaseline = p.phase !== 'draft';
  const tools = p.tools.map((t) => toolView(t, p.startDate, today, cal, useBaseline));
  const toolsEnd = tools.reduce<string>((m, t) => (t.end ? maxDate(m, t.end) : m), p.startDate);
  const milestones = forecastChain(
    p.milestones,
    toolsEnd,
    tools.every((t) => t.done),
    today,
    cal,
    useBaseline,
  );
  const all = [...tools.flatMap((t) => t.steps), ...milestones];
  const lastStep = lastActive(milestones) ?? lastActive(all);
  const end = lastStep?.forecast ?? null;
  const endRef = lastStep?.ref ?? null;
  const counts = { overdue: 0, dueToday: 0, atRisk: 0, hold: 0, done: 0, total: 0 };
  for (const s of all) {
    if (s.status === 'SKIPPED') continue;
    counts.total += 1;
    if (s.status === 'OVERDUE') counts.overdue += 1;
    else if (s.status === 'DUE_TODAY') counts.dueToday += 1;
    else if (s.status === 'AT_RISK') counts.atRisk += 1;
    else if (s.status === 'HOLD') counts.hold += 1;
    else if (s.status === 'DONE' || s.status === 'DONE_LATE') counts.done += 1;
  }
  let status: ProjectStatus | null = null;
  if (p.phase === 'active' || p.phase === 'shipped') {
    if (p.phase === 'shipped' || (lastStep && lastStep.state === 'done')) status = 'SHIPPED';
    else if (end && endRef && end > endRef) status = 'LATE';
    else if (counts.overdue || counts.atRisk || counts.hold) status = 'AT_RISK';
    else status = 'ON_TRACK';
  }
  return {
    id: p.id,
    phase: p.phase,
    tools,
    milestones,
    status,
    end,
    endRef,
    slip: end && endRef ? daysBetween(endRef, end) : 0,
    counts,
  };
}

export type ProjectTotals = Record<ProjectStatus, number> & { total: number; balanced: boolean };

/** Dashboard buckets. They always add up to the number of published projects. */
export function projectTotals(views: readonly ProjectView[]): ProjectTotals {
  const t = { ON_TRACK: 0, AT_RISK: 0, LATE: 0, SHIPPED: 0, total: 0, balanced: true };
  for (const v of views) {
    if (!v.status) continue;
    t[v.status] += 1;
    t.total += 1;
  }
  t.balanced = PROJECT_STATUSES.reduce((n, s) => n + t[s], 0) === t.total;
  return t;
}

/** Where a step sits in its chain, for locks and "who acts next". */
export function chainOf(v: ProjectView, stepId: string): { chain: StepView[]; index: number } | null {
  for (const chain of [...v.tools.map((t) => t.steps), v.milestones]) {
    const index = chain.findIndex((s) => s.id === stepId);
    if (index >= 0) return { chain, index };
  }
  return null;
}

/**
 * A stage owner may correct their own answer until the next step has acted (done or
 * hold). After that only the planner can change it — the next department has already
 * worked on the strength of it.
 */
export function nextStepActed(v: ProjectView, stepId: string): boolean {
  const c = chainOf(v, stepId);
  if (!c) return false;
  let after = c.chain.slice(c.index + 1);
  // The last tool step is followed by the first milestone.
  if (!after.length && v.tools.some((t) => t.steps === c.chain)) after = v.milestones;
  const next = after.find((s) => s.state !== 'skipped');
  return !!next && (next.state === 'done' || next.state === 'hold');
}

export type DoneError = 'bad_date' | 'future_date' | 'skipped' | 'before_start';

/** Can a step be marked done on `date`? */
export function validateDone(
  s: Pick<StepInput, 'state'>,
  date: string,
  today: string,
  earliest?: string,
): DoneError | null {
  if (!isValidDate(date)) return 'bad_date';
  if (s.state === 'skipped') return 'skipped';
  if (date > today) return 'future_date';
  if (earliest && date < earliest) return 'before_start';
  return null;
}

/** Slip of a finished step (actual − baseline), else null. */
export function doneSlip(s: StepInput): number | null {
  const ref = s.baseline ?? s.planned;
  return s.state === 'done' && s.actual && ref ? daysBetween(ref, s.actual) : null;
}

/** Severity order for sorting work lists: most urgent first. */
export const STATUS_RANK: Record<StepStatus, number> = {
  OVERDUE: 0,
  HOLD: 1,
  DUE_TODAY: 2,
  AT_RISK: 3,
  UPCOMING: 4,
  DONE_LATE: 5,
  DONE: 6,
  SKIPPED: 7,
};

export const PROJECT_RANK: Record<ProjectStatus, number> = { LATE: 0, AT_RISK: 1, ON_TRACK: 2, SHIPPED: 3 };
